import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Collapse,
  IconButton,
  TextareaAutosize,
  Tabs,
  Tab,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  ExpandLess,
  ExpandMore,
  Upload,
  Download,
  TuneRounded,
  HighlightOff,
  ContentPaste,
  Bolt,
  Pause,
  PlayArrow,
} from '@mui/icons-material';
import CarrierCard from '../CarrierCard/CarrierCard';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import { saveCarrierExport, downloadCarrierCsv } from '../../utils/carrierExport';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const defaultFilters = () => ({
  skipBrokers: false,
  skipInactive: false,
  showInactiveOnly: false,
  activeDuration: 0,
  resultLimit: 10,
  state: '',
  states: [],
  requireEmail: false,
  requirePhone: false,
  requireMC: false,
  requireCommonAuthority: false,
  operationScope: 'any',
  authorityState: 'any',
  minPowerUnits: 0,
  minDrivers: 0,
  minCDLDrivers: 0,
  cargoKeyword: '',
  businessType: 'any',
  pendingInsuranceOnly: false,
  revocationOnly: false,
  minCoverageAmount: 0,
});

const SearchDashboard = ({
  onSearch,
  onBatchSearch,
  clearBatchResults,
  onPauseBatchSearch,
  onResumeBatchSearch,
  loading,
  error,
  carrierData,
  showBasicInfo,
  onToggleView,
  onQuickEmail,
  isBatchPaused = false,
  isBatchSearching = false,
  autoHideOnSearch = true,
  showExportActions = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('auto');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [exportNotice, setExportNotice] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [batchInput, setBatchInput] = useState('');
  const [showSearchPanel, setShowSearchPanel] = useState(true);
  // Track if user explicitly hid the search panel — persists across tab navigation
  const userHidSearchRef = useRef(false);
  const [filters, setFilters] = useState(defaultFilters);

  const handleSearch = () => {
    if (searchTerm.trim() || searchType === 'range') {
      onSearch(searchTerm.trim(), searchType, filters);
      if (autoHideOnSearch) {
        setShowSearchPanel(false);
        userHidSearchRef.current = true;
      }
    }
  };

  const handleBatchSearch = () => {
    if (batchInput.trim()) {
      onBatchSearch(batchInput.trim(), filters);
      if (autoHideOnSearch) {
        setShowSearchPanel(false);
        userHidSearchRef.current = true;
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (activeTab === 0) {
        handleSearch();
      } else {
        handleBatchSearch();
      }
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => {
      const next = {
        ...prev,
        [filterName]: value
      };
      if (filterName === 'skipInactive' && value) {
        next.showInactiveOnly = false;
      }
      if (filterName === 'showInactiveOnly' && value) {
        next.skipInactive = false;
      }
      return next;
    });
  };

  const toggleState = (stateCode) => {
    setFilters(prev => {
      const exists = prev.states.includes(stateCode);
      return {
        ...prev,
        states: exists ? prev.states.filter((s) => s !== stateCode) : [...prev.states, stateCode]
      };
    });
  };

  const toggleBooleanFilter = (filterName) => {
    handleFilterChange(filterName, !filters[filterName]);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setBatchInput('');
    setFilters(defaultFilters());
  };

  const quickSearch = (type, value) => {
    setSearchType(type);
    setSearchTerm(value);
    setTimeout(() => handleSearch(), 100);
  };

  const detectSearchType = (input) => {
    const trimmed = input.trim();

    if (/^MC-?\d{6,8}$/i.test(trimmed) || /^\d{6,8}$/.test(trimmed)) {
      return 'mc';
    }
    if (/^\d{7,8}$/.test(trimmed)) {
      return 'dot';
    }
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return 'email';
    }
    if (/^\d+-\d+$/.test(trimmed)) {
      return 'range';
    }
    if (/^[a-zA-Z\s.,&]+$/.test(trimmed) && trimmed.length > 2) {
      return 'name';
    }
    return 'auto';
  };

  const searchTypes = [
    { id: 'auto', label: 'Auto Detect', placeholder: 'Enter MC, DOT, email, or name...', example: 'MC-123456' },
    { id: 'mc', label: 'MC Number', placeholder: 'Enter MC number...', example: '123456' },
    { id: 'dot', label: 'DOT Number', placeholder: 'Enter DOT number...', example: '2874689' },
    { id: 'email', label: 'Email', placeholder: 'Enter email address...', example: 'carrier@company.com' },
    { id: 'name', label: 'Company Name', placeholder: 'Enter company name...', example: 'ABC Trucking' },
    { id: 'range', label: 'Number Range', placeholder: 'e.g., 100000-100050', example: '100000-100050' }
  ];

  const quickExamples = [
    { type: 'mc', label: 'MC Search', value: '123456' },
    { type: 'dot', label: 'DOT Search', value: '2874689' },
    { type: 'range', label: 'Range Search', value: '100000-100010' },
    { type: 'email', label: 'Email Search', value: 'carrier@example.com' },
    { type: 'name', label: 'Name Search', value: 'ABC Trucking' }
  ];

  const batchExamples = `123456
2874689
MC-654321
carrier@example.com
ABC Trucking
100000-100005`;
// inside SearchDashboard component
 const exportCSV = (payload) => {
   const record = saveCarrierExport(payload);
   if (!record || !record.count) return;
   setExportNotice(`Saved ${record.count} carriers for Campaign/Compose (${new Date(record.createdAt).toLocaleString()}).`);
 };

 const downloadCSV = (payload) => {
   const record = downloadCarrierCsv(payload);
   if (!record || !record.count) return;
   setExportNotice(`Downloaded CSV with ${record.count} carriers.`);
 };
const exportPDF = async (payload, selectedStates = []) => {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  // Normalize rows
  const rows = payload?.searchType === 'batch'
    ? (payload.batchResults || [])
    : (payload ? [payload] : []);

  if (!rows.length) return;

  // Group by state (prefer physical, then mailing, else 'Unknown')
  const groupKey = (c) => (c.phyState || c.mailState || 'Unknown').toUpperCase();
  const grouped = {};
  rows.forEach(c => {
    const key = groupKey(c);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  });

  // If multi-select applied, order by selectedStates first; else alpha
  const order = (Array.isArray(selectedStates) && selectedStates.length)
    ? [...selectedStates.map(s => s.toUpperCase()), ...Object.keys(grouped).filter(k => !selectedStates.map(s=>s.toUpperCase()).includes(k)).sort()]
    : Object.keys(grouped).sort();

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  const columns = [
    { header: 'Legal Name', dataKey: 'legalName' },
    { header: 'DBA', dataKey: 'dbaName' },
    { header: 'DOT', dataKey: 'dotNumber' },
    { header: 'MC', dataKey: 'mcNumber' },
    { header: 'Phone', dataKey: 'phone' },
    { header: 'Email', dataKey: 'email' },
    { header: 'Op', dataKey: 'carrierOperation' },
    { header: 'Units', dataKey: 'powerUnits' },
    { header: 'Drivers', dataKey: 'totalDrivers' },
    { header: 'Status', dataKey: 'status' },
  ];

  let firstPage = true;

  for (const st of order) {
    const data = grouped[st];
    if (!data || !data.length) continue;

    if (!firstPage) doc.addPage();
    firstPage = false;

    // Title
    doc.setFontSize(16);
    doc.text(`Carriers — ${st}`, 40, 40);

    // Subline (date & count)
    doc.setFontSize(10);
    doc.text(`Exported: ${new Date().toLocaleString()}  |  Records: ${data.length}`, 40, 58);

    // Table
    autoTable(doc, {
      startY: 75,
      head: [columns.map(c => c.header)],
      body: data.map(c => columns.map(col => c[col.dataKey] ?? '')),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fontStyle: 'bold' },
      margin: { left: 40, right: 40 },
      tableWidth: 'auto',
      didDrawPage: (d) => {
        // Footer page number
        const str = `Page ${doc.getCurrentPageInfo().pageNumber}`;
        doc.setFontSize(9);
        doc.text(str, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 20);
      }
    });

    // Optional: Addresses table (second table on same page)
     autoTable(doc, {
       startY: doc.lastAutoTable.finalY + 12,
      head: [['Physical Address', 'Mailing Address']],
      body: data.map(c => [c.physicalAddress || '', c.mailingAddress || '']),
      styles: { fontSize: 8, cellPadding: 4 },
      margin: { left: 40, right: 40 },
     });
  }

  const ts = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
  doc.save(`carriers-statewise-${ts}.pdf`);
};

  const durationMarks = [
    { value: 0, label: 'Any' },
    { value: 6, label: '6M' },
    { value: 12, label: '1Y' },
    { value: 24, label: '2Y' },
    { value: 60, label: '5Y' }
  ];

  const activeFilterCount = [
    filters.skipBrokers,
    filters.skipInactive,
    filters.showInactiveOnly,
    filters.activeDuration > 0,
    filters.resultLimit !== 10,
    filters.states.length > 0,
    filters.requireEmail,
    filters.requirePhone,
    filters.requireMC,
    filters.requireCommonAuthority,
    filters.operationScope !== 'any',
    filters.authorityState !== 'any',
    filters.minPowerUnits > 0,
    filters.minDrivers > 0,
    filters.minCDLDrivers > 0,
    filters.cargoKeyword.trim().length > 0,
    filters.businessType !== 'any',
    filters.pendingInsuranceOnly,
    filters.revocationOnly,
    filters.minCoverageAmount > 0,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  const isBatchActive = carrierData && carrierData.searchType === 'batch';
  const showLiveResults = Boolean(carrierData);

  return (
    <Box sx={{
      px: { xs: 1, sm: 2, md: 3 },
      py: 2,
      maxWidth: '100%',
      mx: 0
    }}>
      {!showSearchPanel && carrierData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
            <Button
              size="small"
              variant="contained"
              onClick={() => { setShowSearchPanel(true); userHidSearchRef.current = false; }}
              startIcon={<ExpandMore />}
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                px: 2.5,
                py: 0.6,
                borderRadius: 999,
                textTransform: 'none',
                boxShadow: '0 8px 18px rgba(102,126,234,0.25)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 12px 22px rgba(102,126,234,0.35)'
                }
              }}
            >
              Show Search
            </Button>
            {isBatchActive && isBatchSearching && (
              <Button
                size="small"
                variant="outlined"
                color={isBatchPaused ? 'success' : 'warning'}
                onClick={isBatchPaused ? onResumeBatchSearch : onPauseBatchSearch}
                startIcon={isBatchPaused ? <PlayArrow /> : <Pause />}
                sx={{ borderRadius: 999, fontWeight: 700, textTransform: 'none' }}
              >
                {isBatchPaused ? 'Resume Search' : 'Pause Search'}
              </Button>
            )}
          </Stack>
        </Box>
      )}

      <Collapse in={showSearchPanel} unmountOnExit>
        {/* Main Search Card */}
        <Card sx={{
          mb: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          border: '1px solid rgba(102,126,234,0.12)',
          overflow: 'hidden',
          borderRadius: 3,
        }}>
          {/* Modern Tab Header */}
          <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: 2, pb: 0, background: 'linear-gradient(135deg, rgba(102,126,234,0.04), rgba(118,75,162,0.04))' }}>
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
              {[
                { label: 'Single Search', icon: <SearchIcon sx={{ fontSize: 18 }} />, desc: 'Search one carrier at a time' },
                { label: 'Batch Search', icon: <ContentPaste sx={{ fontSize: 18 }} />, desc: 'Process multiple lookups at once' },
              ].map((tab, idx) => (
                <Paper
                  key={idx}
                  elevation={0}
                  onClick={() => setActiveTab(idx)}
                  sx={{
                    flex: 1,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2.5,
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    border: '2px solid',
                    borderColor: activeTab === idx ? '#667eea' : 'transparent',
                    background: activeTab === idx
                      ? 'linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.08))'
                      : 'rgba(0,0,0,0.02)',
                    '&:hover': {
                      background: activeTab === idx
                        ? 'linear-gradient(135deg, rgba(102,126,234,0.12), rgba(118,75,162,0.1))'
                        : 'rgba(102,126,234,0.05)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      sx={{
                        width: 32, height: 32,
                        bgcolor: activeTab === idx ? '#667eea' : '#e2e8f0',
                        color: activeTab === idx ? 'white' : '#64748b',
                        transition: 'all 0.25s ease',
                      }}
                    >
                      {tab.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={activeTab === idx ? 800 : 600} sx={{ color: activeTab === idx ? '#667eea' : 'text.primary', fontSize: 13 }}>
                        {tab.label}
                      </Typography>
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                          {tab.desc}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {activeTab === 0 && (
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={10}>
                  <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, border: '1px solid rgba(102,126,234,0.15)' }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                          Search Details
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Choose a search type and enter the value. Auto detect works for most inputs.
                        </Typography>
                      </Box>
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontWeight: 500 }}>Search Type</InputLabel>
                            <Select
                              value={searchType}
                              onChange={(e) => setSearchType(e.target.value)}
                              label="Search Type"
                              sx={{
                                fontWeight: 500,
                                '& .MuiOutlinedInput-root': {
                                  transition: 'all 0.2s ease',
                                  '&:hover fieldset': {
                                    borderColor: 'primary.main'
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                  }
                                }
                              }}
                            >
                              {searchTypes.map(type => (
                                <MenuItem key={type.id} value={type.id} sx={{ fontWeight: 500 }}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={8} md={9}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder={searchTypes.find(t => t.id === searchType)?.placeholder}
                            value={searchTerm}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSearchTerm(value);
                              if (searchType === 'auto' && value.trim()) {
                                const detectedType = detectSearchType(value);
                              }
                            }}
                            onKeyPress={handleKeyPress}
                            disabled={loading}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: 'primary.main', mr: 0.5 }} />
                                </InputAdornment>
                              ),
                              endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => setSearchTerm('')}
                                    edge="end"
                                  >
                                    <HighlightOff sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                            helperText={
                              searchType === 'auto' && searchTerm.trim()
                                ? `Detected: ${detectSearchType(searchTerm)}`
                                : `Example: ${searchTypes.find(t => t.id === searchType)?.example}`
                            }
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                transition: 'all 0.2s ease',
                                '&:hover fieldset': {
                                  borderColor: 'primary.main'
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'primary.main',
                                  borderWidth: 2
                                }
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={2}>
                  <Paper elevation={0} sx={{ p: { xs: 1, sm: 1.25 }, borderRadius: 2.5, border: '1px solid rgba(102,126,234,0.15)', height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                        Actions
                      </Typography>
                      <Button
                        size="small"
                        type="submit"
                        variant="contained"
                        onClick={handleSearch}
                        disabled={loading || (!searchTerm.trim() && searchType !== 'range')}
                        startIcon={<SearchIcon />}
                        fullWidth
                        sx={{
                          fontWeight: 600,
                          py: 0.75,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: 2,
                          textTransform: 'none',
                          '&:hover:not(:disabled)': {
                            boxShadow: '0 8px 24px rgba(102,126,234,0.4)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        {loading ? 'Searching...' : 'Search'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={clearSearch}
                        startIcon={<ClearIcon />}
                        fullWidth
                        sx={{ py: 0.6, borderRadius: 2, textTransform: 'none' }}
                      >
                        Clear
                      </Button>
                      <Button
                        size="small"
                        variant={showAdvanced ? 'contained' : 'outlined'}
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        startIcon={<TuneRounded />}
                        fullWidth
                        sx={{ py: 0.6, borderRadius: 2, textTransform: 'none' }}
                      >
                        {showAdvanced ? 'Hide Filters' : 'Filters'}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Paper elevation={0} sx={{ mt: 2.5, p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, border: '1px dashed rgba(102,126,234,0.25)', background: 'rgba(102,126,234,0.02)' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#667eea' }}>
                  Quick Templates
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {quickExamples.map((item, index) => (
                    <Chip
                      key={index}
                      label={item.label}
                      size={isMobile ? "small" : "medium"}
                      variant="outlined"
                      onClick={() => quickSearch(item.type, item.value)}
                      clickable
                      sx={{
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(102,126,234,0.1)',
                          borderColor: 'primary.main',
                          color: 'primary.main'
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Paper>
            </Box>
          )}
{activeTab === 1 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={10}>
                  <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, border: '1px solid rgba(102,126,234,0.15)' }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#667eea15', color: '#667eea' }}>
                          <ContentPaste sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Batch Input
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Paste one search term per line — MC, DOT, email, or name
                          </Typography>
                        </Box>
                      </Stack>
                      <Paper
                        elevation={0}
                        sx={{
                          border: '2px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          p: 0,
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          background: 'rgba(102,126,234,0.02)',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: '0 4px 12px rgba(102,126,234,0.1)'
                          },
                          '&:focus-within': {
                            borderColor: 'primary.main',
                            boxShadow: '0 4px 12px rgba(102,126,234,0.15)'
                          }
                        }}
                      >
                        <TextareaAutosize
                          minRows={isMobile ? 5 : 8}
                          maxRows={isMobile ? 8 : 12}
                          placeholder={`Enter multiple search terms, one per line:

${batchExamples}`}
                          value={batchInput}
                          onChange={(e) => setBatchInput(e.target.value)}
                          onKeyPress={handleKeyPress}
                          style={{
                            width: '100%',
                            padding: '16px',
                            border: 'none',
                            fontSize: '14px',
                            fontFamily: '"Roboto Mono", monospace',
                            resize: 'none',
                            background: 'transparent',
                            color: 'inherit',
                            outline: 'none'
                          }}
                        />
                      </Paper>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={2}>
                  <Paper elevation={0} sx={{ p: { xs: 1, sm: 1.25 }, borderRadius: 2.5, border: '1px solid rgba(102,126,234,0.15)', height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                        Actions
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleBatchSearch}
                        disabled={loading || !batchInput.trim()}
                        startIcon={<SearchIcon />}
                        fullWidth
                        sx={{
                          fontWeight: 600,
                          py: 0.75,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: 2,
                          textTransform: 'none',
                          '&:hover:not(:disabled)': {
                            boxShadow: '0 8px 24px rgba(102,126,234,0.4)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        {loading ? 'Processing...' : `Search ${batchInput.split('\n').filter(l => l.trim()).length} Terms`}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => setBatchInput(batchExamples)}
                        fullWidth
                        sx={{ py: 0.6, borderRadius: 2, textTransform: 'none' }}
                      >
                        Load Example
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ClearIcon />}
                        color="error"
                        onClick={() => {
                          if (clearBatchResults) clearBatchResults();
                          setBatchInput('');
                        }}
                        disabled={!isBatchActive}
                        fullWidth
                        sx={{ py: 0.6, borderRadius: 2, textTransform: 'none' }}
                      >
                        Clear
                      </Button>
                      <Button
                        size="small"
                        variant={showAdvanced ? 'contained' : 'outlined'}
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        startIcon={<TuneRounded />}
                        fullWidth
                        sx={{ py: 0.6, borderRadius: 2, textTransform: 'none' }}
                      >
                        {showAdvanced ? 'Hide Filters' : 'Filters'}
                      </Button>
                      <Chip
                        label={`${batchInput.split('\n').filter(l => l.trim()).length} lines`}
                        size="small"
                        sx={{ bgcolor: 'rgba(102,126,234,0.1)', fontWeight: 600, borderRadius: 2 }}
                      />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Advanced Filters */}
          <Collapse in={showAdvanced}>
            <Box sx={{
              mt: 2.5,
              pt: 2.5,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  border: '1px solid rgba(102,126,234,0.16)',
                  background: 'linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(16,185,129,0.05) 100%)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  spacing={1.5}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.primary'
                      }}
                    >
                      <TuneRounded sx={{ fontSize: 18, color: 'primary.main' }} />
                      Advanced Filters
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Authority, geography, contactability aur fleet size ke basis par search ko narrow karo.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={`${activeFilterCount} active`}
                      color={hasActiveFilters ? 'primary' : 'default'}
                      variant={hasActiveFilters ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 700 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => setFilters(defaultFilters())}
                      disabled={!hasActiveFilters}
                      sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
                    >
                      Reset Filters
                    </Button>
                  </Stack>
                </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2.5,
                      border: '1px solid rgba(102,126,234,0.15)',
                      background: 'rgba(255,255,255,0.78)',
                      height: '100%'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                      Quick Rules
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                      {[
                        { key: 'skipBrokers', label: 'No Brokers', color: 'primary' },
                        { key: 'skipInactive', label: 'USDOT Active', color: 'success' },
                        { key: 'showInactiveOnly', label: 'USDOT Inactive', color: 'warning' },
                        { key: 'requireCommonAuthority', label: 'Common Authority', color: 'success' },
                        { key: 'requireEmail', label: 'Has Email', color: 'info' },
                        { key: 'requirePhone', label: 'Has Phone', color: 'secondary' },
                        { key: 'requireMC', label: 'Has MC', color: 'primary' },
                      ].map((item) => (
                        <Chip
                          key={item.key}
                          label={item.label}
                          onClick={() => toggleBooleanFilter(item.key)}
                          color={filters[item.key] ? item.color : 'default'}
                          variant={filters[item.key] ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700, borderRadius: 999, cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={filters.skipBrokers}
                            onChange={(e) => handleFilterChange('skipBrokers', e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">Exclude broker-only records</Typography>}
                        sx={{
                          m: 0,
                          border: '1px solid rgba(102,126,234,0.2)',
                          borderRadius: 1.75,
                          px: 1,
                          py: 0.35,
                          '&:hover': { background: 'rgba(102,126,234,0.06)' }
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={filters.skipInactive}
                            onChange={(e) => handleFilterChange('skipInactive', e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">USDOT active only</Typography>}
                        sx={{
                          m: 0,
                          border: '1px solid rgba(102,126,234,0.2)',
                          borderRadius: 1.75,
                          px: 1,
                          py: 0.35,
                          '&:hover': { background: 'rgba(102,126,234,0.06)' }
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={filters.showInactiveOnly}
                            onChange={(e) => handleFilterChange('showInactiveOnly', e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">USDOT inactive only</Typography>}
                        sx={{
                          m: 0,
                          border: '1px solid rgba(102,126,234,0.2)',
                          borderRadius: 1.75,
                          px: 1,
                          py: 0.35,
                          '&:hover': { background: 'rgba(102,126,234,0.06)' }
                        }}
                      />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2.5,
                      border: '1px solid rgba(102,126,234,0.15)',
                      background: 'rgba(255,255,255,0.78)',
                      height: '100%'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                      Geography
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                      Multi-state targeting ke liye select box ya quick-pick use karo.
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>States</InputLabel>
                      <Select
                        multiple
                        value={filters.states}
                        label="States"
                        onChange={(e) => handleFilterChange('states', e.target.value)}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {selected.slice(0, 3).map(s => (
                              <Chip key={s} label={s} size="small" sx={{ bgcolor: 'rgba(102,126,234,0.12)' }} />
                            ))}
                            {selected.length > 3 && (
                              <Chip label={`+${selected.length - 3}`} size="small" sx={{ bgcolor: 'rgba(102,126,234,0.12)' }} />
                            )}
                          </Box>
                        )}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main'
                            }
                          }
                        }}
                      >
                        {US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.25 }}>
                      {['TX', 'CA', 'FL', 'IL', 'GA', 'NJ', 'PA', 'OH'].map((stateCode) => (
                        <Chip
                          key={stateCode}
                          label={stateCode}
                          onClick={() => toggleState(stateCode)}
                          color={filters.states.includes(stateCode) ? 'primary' : 'default'}
                          variant={filters.states.includes(stateCode) ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 700, cursor: 'pointer' }}
                        />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                      <Button size="small" variant="text" onClick={() => handleFilterChange('states', ['TX', 'CA', 'FL', 'IL'])} sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Load Hot States
                      </Button>
                      <Button size="small" variant="text" onClick={() => handleFilterChange('states', [])} sx={{ textTransform: 'none', fontWeight: 700 }}>
                        Clear States
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2.5,
                      border: '1px solid rgba(102,126,234,0.15)',
                      background: 'rgba(255,255,255,0.78)',
                      height: '100%'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                      Operations & Authority
                    </Typography>
                    <Grid container spacing={1.25}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Operation Scope</InputLabel>
                          <Select value={filters.operationScope} label="Operation Scope" onChange={(e) => handleFilterChange('operationScope', e.target.value)}>
                            <MenuItem value="any">Any</MenuItem>
                            <MenuItem value="interstate">Interstate</MenuItem>
                            <MenuItem value="intrastate">Intrastate</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Authority State</InputLabel>
                          <Select value={filters.authorityState} label="Authority State" onChange={(e) => handleFilterChange('authorityState', e.target.value)}>
                            <MenuItem value="any">Any</MenuItem>
                            <MenuItem value="active">Active Common Authority</MenuItem>
                            <MenuItem value="inactive">Not Active / No Authority</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            mt: 0.5,
                            p: 1.25,
                            borderRadius: 2,
                            border: '1px solid rgba(102,126,234,0.14)',
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.94), rgba(102,126,234,0.04))'
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.25}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                          >
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Minimum Active Duration
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Carrier authority age ko months me control karo.
                              </Typography>
                            </Box>
                            <TextField
                              size="small"
                              type="number"
                              label="Months"
                              value={filters.activeDuration}
                              onChange={(e) => {
                                const next = Math.min(60, Math.max(0, Number(e.target.value) || 0));
                                handleFilterChange('activeDuration', next - (next % 6));
                              }}
                              sx={{ width: { xs: '100%', sm: 120 } }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">M</InputAdornment>
                              }}
                            />
                          </Stack>
                          <Slider
                            size="medium"
                            value={filters.activeDuration}
                            onChange={(e, newValue) => handleFilterChange('activeDuration', newValue)}
                            min={0}
                            max={60}
                            step={6}
                            marks={durationMarks}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => value === 0 ? 'Any' : `${value}M`}
                            sx={{
                              px: 1,
                              color: '#667eea',
                              '& .MuiSlider-thumb': {
                                width: 18,
                                height: 18,
                                boxShadow: '0 4px 12px rgba(102,126,234,0.28)'
                              },
                              '& .MuiSlider-track': {
                                border: 'none',
                                height: 6
                              },
                              '& .MuiSlider-rail': {
                                height: 6,
                                opacity: 0.25
                              },
                              '& .MuiSlider-markLabel': {
                                fontSize: 11
                              }
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2.5,
                      border: '1px solid rgba(102,126,234,0.15)',
                      background: 'rgba(255,255,255,0.78)',
                      height: '100%'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                      Cargo & Compliance
                    </Typography>
                    <Grid container spacing={1.25}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Cargo Keyword"
                          value={filters.cargoKeyword}
                          onChange={(e) => handleFilterChange('cargoKeyword', e.target.value)}
                          placeholder="General Freight, Produce, Chemicals"
                          helperText="Filter by cargo classification text"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Business Type</InputLabel>
                          <Select
                            value={filters.businessType}
                            label="Business Type"
                            onChange={(e) => handleFilterChange('businessType', e.target.value)}
                          >
                            <MenuItem value="any">Any</MenuItem>
                            <MenuItem value="individual">Individual</MenuItem>
                            <MenuItem value="partnership">Partnership</MenuItem>
                            <MenuItem value="corporation">Corporation</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Min Insurance Coverage"
                          value={filters.minCoverageAmount}
                          onChange={(e) => handleFilterChange('minCoverageAmount', Math.max(0, Number(e.target.value) || 0))}
                          helperText="USD, e.g. 750000"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <FormControlLabel
                            control={<Checkbox size="small" checked={filters.pendingInsuranceOnly} onChange={(e) => handleFilterChange('pendingInsuranceOnly', e.target.checked)} />}
                            label={<Typography variant="body2">Pending insurance only</Typography>}
                            sx={{ m: 0, border: '1px solid rgba(102,126,234,0.2)', borderRadius: 1.75, px: 1, py: 0.35 }}
                          />
                          <FormControlLabel
                            control={<Checkbox size="small" checked={filters.revocationOnly} onChange={(e) => handleFilterChange('revocationOnly', e.target.checked)} />}
                            label={<Typography variant="body2">Revocation flags only</Typography>}
                            sx={{ m: 0, border: '1px solid rgba(102,126,234,0.2)', borderRadius: 1.75, px: 1, py: 0.35 }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 2.5,
                      border: '1px solid rgba(102,126,234,0.15)',
                      background: 'rgba(255,255,255,0.78)'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                      Contact & Fleet Thresholds
                    </Typography>
                    <Grid container spacing={1.25}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Max Results"
                          value={filters.resultLimit}
                          onChange={(e) => handleFilterChange('resultLimit', Math.max(1, Number(e.target.value) || 1))}
                          helperText="Custom result limit"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">rows</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Min Power Units"
                          value={filters.minPowerUnits}
                          onChange={(e) => handleFilterChange('minPowerUnits', Math.max(0, Number(e.target.value) || 0))}
                          helperText="Fleet size threshold"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Min Drivers"
                          value={filters.minDrivers}
                          onChange={(e) => handleFilterChange('minDrivers', Math.max(0, Number(e.target.value) || 0))}
                          helperText="All drivers"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Min CDL Drivers"
                          value={filters.minCDLDrivers}
                          onChange={(e) => handleFilterChange('minCDLDrivers', Math.max(0, Number(e.target.value) || 0))}
                          helperText="Commercial license count"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                    ACTIVE FILTERS
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {filters.skipBrokers && (
                      <Chip
                        label="No Brokers"
                        size="small"
                        onDelete={() => handleFilterChange('skipBrokers', false)}
                        color="primary"
                      />
                    )}
                    {filters.skipInactive && (
                      <Chip
                        label="USDOT Active Only"
                        size="small"
                        onDelete={() => handleFilterChange('skipInactive', false)}
                        color="success"
                      />
                    )}
                    {filters.showInactiveOnly && (
                      <Chip
                        label="USDOT Inactive Only"
                        size="small"
                        onDelete={() => handleFilterChange('showInactiveOnly', false)}
                        color="warning"
                      />
                    )}
                    {filters.requireCommonAuthority && (
                      <Chip
                        label="Common Authority"
                        size="small"
                        onDelete={() => handleFilterChange('requireCommonAuthority', false)}
                        color="success"
                      />
                    )}
                    {filters.requireEmail && (
                      <Chip
                        label="Has Email"
                        size="small"
                        onDelete={() => handleFilterChange('requireEmail', false)}
                        color="info"
                      />
                    )}
                    {filters.requirePhone && (
                      <Chip
                        label="Has Phone"
                        size="small"
                        onDelete={() => handleFilterChange('requirePhone', false)}
                      />
                    )}
                    {filters.requireMC && (
                      <Chip
                        label="Has MC"
                        size="small"
                        onDelete={() => handleFilterChange('requireMC', false)}
                      />
                    )}
                    {filters.activeDuration > 0 && (
                      <Chip
                        label={`Min ${filters.activeDuration}M`}
                        size="small"
                        onDelete={() => handleFilterChange('activeDuration', 0)}
                        color="warning"
                      />
                    )}
                    {filters.states.length > 0 && (
                      <Chip
                        label={`${filters.states.length} State${filters.states.length > 1 ? 's' : ''}`}
                        size="small"
                        onDelete={() => handleFilterChange('states', [])}
                      />
                    )}
                    {filters.operationScope !== 'any' && (
                      <Chip
                        label={filters.operationScope === 'interstate' ? 'Interstate' : 'Intrastate'}
                        size="small"
                        onDelete={() => handleFilterChange('operationScope', 'any')}
                      />
                    )}
                    {filters.authorityState !== 'any' && (
                      <Chip
                        label={filters.authorityState === 'active' ? 'Authority Active' : 'Authority Inactive'}
                        size="small"
                        onDelete={() => handleFilterChange('authorityState', 'any')}
                      />
                    )}
                    {filters.businessType !== 'any' && (
                      <Chip
                        label={filters.businessType}
                        size="small"
                        onDelete={() => handleFilterChange('businessType', 'any')}
                      />
                    )}
                    {filters.cargoKeyword.trim() && (
                      <Chip
                        label={`Cargo: ${filters.cargoKeyword.trim()}`}
                        size="small"
                        onDelete={() => handleFilterChange('cargoKeyword', '')}
                      />
                    )}
                    {filters.pendingInsuranceOnly && (
                      <Chip
                        label="Pending Insurance"
                        size="small"
                        onDelete={() => handleFilterChange('pendingInsuranceOnly', false)}
                        color="warning"
                      />
                    )}
                    {filters.revocationOnly && (
                      <Chip
                        label="Revocation Flags"
                        size="small"
                        onDelete={() => handleFilterChange('revocationOnly', false)}
                        color="error"
                      />
                    )}
                    {filters.minPowerUnits > 0 && (
                      <Chip
                        label={`Units ${filters.minPowerUnits}+`}
                        size="small"
                        onDelete={() => handleFilterChange('minPowerUnits', 0)}
                      />
                    )}
                    {filters.minDrivers > 0 && (
                      <Chip
                        label={`Drivers ${filters.minDrivers}+`}
                        size="small"
                        onDelete={() => handleFilterChange('minDrivers', 0)}
                      />
                    )}
                    {filters.minCDLDrivers > 0 && (
                      <Chip
                        label={`CDL ${filters.minCDLDrivers}+`}
                        size="small"
                        onDelete={() => handleFilterChange('minCDLDrivers', 0)}
                      />
                    )}
                    {filters.minCoverageAmount > 0 && (
                      <Chip
                        label={`Coverage $${filters.minCoverageAmount}+`}
                        size="small"
                        onDelete={() => handleFilterChange('minCoverageAmount', 0)}
                      />
                    )}
                    {filters.resultLimit !== 10 && (
                      <Chip
                        label={`Max ${filters.resultLimit}`}
                        size="small"
                        onDelete={() => handleFilterChange('resultLimit', 10)}
                      />
                    )}
                  </Box>
                </Box>
              )}
              </Paper>
            </Box>
          </Collapse>
          </CardContent>
        </Card>
      </Collapse>

      {/* Error Alert */}
      {error && (
        <Alert
          severity={error.includes('found') ? 'warning' : 'error'}
          sx={{
            mb: 3,
            fontWeight: 500,
            borderRadius: 1.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          {error}
        </Alert>
      )}

      {/* Export Buttons */}
      {showExportActions && carrierData && (
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1}
          sx={{ mb: 3, justifyContent: 'flex-end' }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<Download />}
            onClick={() => exportCSV(carrierData)}
            sx={{ fontWeight: 600 }}
          >
            Save for Campaign/Compose
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Download />}
            onClick={() => downloadCSV(carrierData)}
            sx={{ fontWeight: 600 }}
          >
            Export CSV
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Download />}
            onClick={() => exportPDF(carrierData, filters.states)}
            sx={{
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            Export PDF
          </Button>
        </Stack>
      )}
      {showExportActions && exportNotice && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {exportNotice}
        </Alert>
      )}

      {/* Results Section */}
      <Box sx={{ minHeight: '400px' }}>
        {loading && !showLiveResults ? (
          <SkeletonLoader />
        ) : showLiveResults ? (
          <Stack spacing={1.5}>
            {isBatchActive && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid rgba(102,126,234,0.16)',
                  background: isBatchPaused
                    ? 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(249,115,22,0.08))'
                    : 'linear-gradient(135deg, rgba(102,126,234,0.08), rgba(16,185,129,0.08))',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={1}
                >
                  <Box>
                    <Typography variant="body1" fontWeight={800}>
                      {isBatchSearching ? (isBatchPaused ? 'Batch Search Paused' : 'Batch Search Running') : 'Batch Search Complete'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Results live update ho rahe hain. User inhein search ke doraan bhi dekh sakta hai.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {isBatchSearching && (
                      <Button
                        size="small"
                        variant="outlined"
                        color={isBatchPaused ? 'success' : 'warning'}
                        onClick={isBatchPaused ? onResumeBatchSearch : onPauseBatchSearch}
                        startIcon={isBatchPaused ? <PlayArrow /> : <Pause />}
                        sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                      >
                        {isBatchPaused ? 'Resume' : 'Pause'}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => { setShowSearchPanel((prev) => !prev); userHidSearchRef.current = false; }}
                      sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                    >
                      {showSearchPanel ? 'Hide Search Panel' : 'Show Search Panel'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}
          <CarrierCard
            data={carrierData}
            showBasicInfo={showBasicInfo}
            onToggleView={onToggleView}
            onQuickEmail={onQuickEmail}
          />
          </Stack>
        ) : !carrierData ? (
          <Card sx={{
            textAlign: 'center',
            py: { xs: 6, sm: 8 },
            background: 'linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.05) 100%)',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: '#667eea15', color: '#667eea', mx: 'auto', mb: 2 }}>
              <SearchIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Typography variant={isMobile ? "body1" : "h6"} sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {activeTab === 0 ? 'Ready to Search' : 'Ready for Batch Processing'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {activeTab === 0
                ? 'Enter MC, DOT, email, or company name to discover carriers'
                : 'Paste multiple search terms to process in bulk'}
            </Typography>
          </Card>
        ) : (
          <CarrierCard
            data={carrierData}
            showBasicInfo={showBasicInfo}
            onToggleView={onToggleView}
            onQuickEmail={onQuickEmail}
          />
        )}
      </Box>
    </Box>
  );
};

export default SearchDashboard;
